import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { computeMastersPathFromEloPoints, upsertMyNickname } from '../lib/supabaseProfile';

function Header({
  language,
  onLanguageChange,
  user,
  profile,
  onRequestLogin,
  onOpenAccount,
  onNavigateHome,
  onProfileUpdated
}) {
  const nickname = useMemo(() => {
    const fromProfile = (profile && (profile.nickname || profile.display_name)) || '';
    const fromMeta = (user && (user.user_metadata?.nickname || user.user_metadata?.display_name)) || '';
    const fromEmail = user && user.email ? String(user.email).split('@')[0] : '';
    return String(fromProfile || fromMeta || fromEmail || '').trim();
  }, [profile, user]);

  const eloPoints = Number(profile?.eloPoints ?? profile?.elo_points ?? 0);
  const userLevel = Number(profile?.computedLevel ?? profile?.levelComputed ?? 0) || 1;
  const rankKey = String(profile?.computedRankKey || profile?.rankKey || profile?.mastersPath?.rankKey || 'explorer');
  const mastersPath = profile?.mastersPath || computeMastersPathFromEloPoints(eloPoints);

  const rankLabel = useMemo(() => {
    const ro = {
      explorer: 'Explorator',
      apprentice: 'Ucenic',
      strategist: 'Strateg',
      champion: 'Campion',
      master: 'Maestru',
      legend: 'Legendă'
    };
    const en = {
      explorer: 'Explorer',
      apprentice: 'Apprentice',
      strategist: 'Strategist',
      champion: 'Champion',
      master: 'Master',
      legend: 'Legend'
    };
    const dict = language === 'ro' ? ro : en;
    return dict[rankKey] || dict.explorer;
  }, [language, rankKey]);

  const rawProgress = Number(mastersPath?.progressToNext || 0);
  const pointsNow = Number(mastersPath?.points || 0);
  const nextRequired = Number(mastersPath?.nextRequired || 0);
  const pointsToNext = Number(mastersPath?.pointsToNext || 0);
  // At exact level-up thresholds, progressToNext can be 0 by definition.
  // For UX, show a forward progress percent based on current points toward next threshold.
  const displayProgress =
    rawProgress === 0 && pointsNow > 0 && pointsToNext > 0 && nextRequired > 0
      ? Math.min(1, Math.max(0, pointsNow / nextRequired))
      : rawProgress;
  const progressPct = Math.round(displayProgress * 100);
  const progressTitle = mastersPath
    ? mastersPath.isMaxDefinedLevel
      ? language === 'ro'
        ? 'Ai atins pragul maxim definit momentan.'
        : 'You reached the highest defined threshold for now.'
      : language === 'ro'
        ? `Progres: ${progressPct}%. Încă ${mastersPath.pointsToNext} puncte IQ până la nivelul ${userLevel + 1}.`
        : `Progress: ${progressPct}%. ${mastersPath.pointsToNext} IQ Points to Level ${userLevel + 1}.`
    : '';

  const [menuOpen, setMenuOpen] = useState(false);
  const [masterPathOpen, setMasterPathOpen] = useState(false);
  const [nickDraft, setNickDraft] = useState('');
  const [savingNick, setSavingNick] = useState(false);
  const [nickMsg, setNickMsg] = useState('');
  const [nickErr, setNickErr] = useState('');
  const menuRef = useRef(null);
  const masterVideoRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    setNickDraft(String(nickname || '').trim());
  }, [nickname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocDown = (e) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!masterPathOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === 'Escape') setMasterPathOpen(false);
    };
    const t = window.setTimeout(() => {
      const v = masterVideoRef.current;
      if (!v) return;
      v.muted = true;
      v.playsInline = true;
      const maybePromise = v.play();
      if (maybePromise && typeof maybePromise.catch === 'function') {
        maybePromise.catch(() => {});
      }
    }, 50);
    document.addEventListener('keydown', onEsc);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onEsc);
    };
  }, [masterPathOpen]);

  useEffect(() => {
    const root = document.documentElement;
    const updateHeaderHeight = () => {
      const nextHeight = headerRef.current ? Math.round(headerRef.current.offsetHeight) : 0;
      if (nextHeight > 0) {
        root.style.setProperty('--app-header-height', `${nextHeight}px`);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    let observer = null;
    if (typeof ResizeObserver !== 'undefined' && headerRef.current) {
      observer = new ResizeObserver(() => updateHeaderHeight());
      observer.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      if (observer) observer.disconnect();
    };
  }, [language, user, nickname, progressPct, rankLabel]);

  const masterPathCopy = language === 'ro'
    ? {
        cta: 'Calea Maestrului',
        title: 'Calea Maestrului: De la Pion la Legendă!',
        subtitle: 'Traseul tău personal spre excelența în șah. Câștigă puncte. Deblochează niveluri. Devino Legendă!',
        lead: 'Ai visat vreodată să gândești ca un maestru? Aici, fiecare pas devine progres real.',
        bullets: [
          'Finalizezi lecții și câștigi IQ Points.',
          'Rezolvi exerciții și urci în nivel pe Calea Maestrului.',
          'Primești provocări noi pe măsură ce avansezi.'
        ],
        accountNote: 'Pentru a salva progresul și nivelul tău, ai nevoie de cont.',
        accountCta: 'Creează cont',
        accountCtaLogged: 'Deschide contul',
        close: 'Închide'
      }
    : {
        cta: 'Master Path',
        title: "Master's Path: From Pawn to Legend!",
        subtitle: 'Your personal path to chess excellence. Earn points. Unlock levels. Become a Legend!',
        lead: 'Want to think like a master? Every step here becomes visible progress.',
        bullets: [
          'Finish lessons and earn IQ Points.',
          "Solve exercises and level up on the Master's Path.",
          'Unlock new challenges as you grow.'
        ],
        accountNote: 'Create an account to save your progress and level.',
        accountCta: 'Create account',
        accountCtaLogged: 'Open account',
        close: 'Close'
      };

  const launchCopy = language === 'ro'
    ? {
        badge: 'Offer • Founding 500',
        label: 'Nu pierde oferta: Starter 2€ • Premium 4€'
      }
    : {
        badge: 'Offer • Founding 500',
        label: "Don't lose it: Starter €2 • Premium €4"
      };

  const mapProfileErrorMessage = (err) => {
    const raw = String(err?.message || err || '').toLowerCase();
    if (!raw) {
      return language === 'ro'
        ? 'Nu am putut salva acum. Încearcă din nou în câteva minute.'
        : 'We could not save right now. Please try again in a few minutes.';
    }
    if (raw.includes('duplicate') || raw.includes('already')) {
      return language === 'ro'
        ? 'Aliasul este deja folosit. Alege unul diferit.'
        : 'This nickname is already taken. Please choose another one.';
    }
    if (raw.includes('network') || raw.includes('failed to fetch') || raw.includes('timeout')) {
      return language === 'ro'
        ? 'Conexiunea este instabilă. Încearcă din nou.'
        : 'Connection is unstable. Please try again.';
    }
    if (raw.includes('unauthorized') || raw.includes('invalid jwt') || raw.includes('forbidden')) {
      return language === 'ro'
        ? 'Sesiunea a expirat. Reautentifică-te și încearcă din nou.'
        : 'Your session expired. Please sign in again and retry.';
    }
    return language === 'ro'
      ? 'Nu am putut salva aliasul acum. Te rugăm să încerci din nou.'
      : 'We could not save your nickname right now. Please try again.';
  };

  return (
    <header ref={headerRef}>
      <div
        className="logo"
        style={{ cursor: onNavigateHome ? 'pointer' : 'default' }}
        onClick={() => onNavigateHome && onNavigateHome()}
        role={onNavigateHome ? 'button' : undefined}
        tabIndex={onNavigateHome ? 0 : undefined}
        onKeyDown={(e) => {
          if (!onNavigateHome) return;
          if (e.key === 'Enter' || e.key === ' ') onNavigateHome();
        }}
        aria-label="ChessMindset.academy"
      >
        Chess<span>Mindset</span>.academy
      </div>
      <div className="header-right">
        <button
          type="button"
          className="header-launch-pill"
          onClick={() => {
            if (typeof onOpenAccount === 'function') onOpenAccount('billing');
            else if (onRequestLogin) onRequestLogin();
          }}
          title={launchCopy.label}
          aria-label={launchCopy.label}
        >
          <span className="header-launch-badge">{launchCopy.badge}</span>
          <span className="header-launch-text">{launchCopy.label}</span>
        </button>

        <select
          className="header-lang-select"
          value={language}
          onChange={(e) => onLanguageChange(e.target.value)}
          aria-label="Language"
          title="Language"
        >
          <option value="ro">RO</option>
          <option value="en">EN</option>
        </select>

        <button
          className="header-masterpath-attention"
          onClick={() => setMasterPathOpen(true)}
          type="button"
          title={masterPathCopy.cta}
          aria-label={masterPathCopy.cta}
        >
          <span className="header-masterpath-dot" />
          {masterPathCopy.cta}
        </button>

        <div className="header-account" ref={menuRef}>
          <button
            className="header-auth-btn"
            onClick={() => {
              if (!user) return onRequestLogin && onRequestLogin();
              if (typeof onOpenAccount === 'function') return onOpenAccount('overview');
              setNickMsg('');
              setNickErr('');
              setMenuOpen((v) => !v);
            }}
          >
            {user ? (
              <span className="header-account-trigger">
                <span className="header-account-main">
                  <span className="header-account-name">
                    {nickname ? (
                      nickname
                    ) : (
                      <span style={{ color: '#b45309' }}>
                        {language === 'ro' ? 'Setează alias' : 'Set nickname'}
                      </span>
                    )}
                  </span>
                  {mastersPath && (
                    <span
                      className="header-mini-progress"
                      title={progressTitle}
                      aria-label={progressTitle}
                    >
                      <span className="header-mini-progress-track">
                        <span className="header-mini-progress-fill" style={{ width: `${progressPct}%` }} />
                      </span>
                      <span className="header-mini-progress-pct">{progressPct}%</span>
                    </span>
                  )}
                </span>
                <span
                  className="header-account-rank-pill"
                  title={language === 'ro' ? 'Calea Maestrului: nivel și puncte' : "The Master's Path: level and points"}
                >
                  {rankLabel} {language === 'ro' ? 'Nivel' : 'Level'} {userLevel}
                  <span style={{ opacity: 0.8 }}>•</span>
                  {eloPoints} IQ Points
                </span>
              </span>
            ) : (
              language === 'ro' ? 'Intră' : 'Sign in'
            )}
          </button>

          {user && menuOpen && (
            <div className="header-account-menu" role="dialog" aria-label={language === 'ro' ? 'Cont' : 'Account'}>
              <div className="header-account-title">
                {language === 'ro' ? 'Calea Maestrului' : "The Master's Path"}
              </div>

              <div className="header-account-subtitle">
                <span style={{ fontWeight: 900 }}>
                  {rankLabel} {language === 'ro' ? 'Nivel' : 'Level'} {userLevel}
                </span>
                <span style={{ opacity: 0.75 }}>
                  {eloPoints} {language === 'ro' ? 'puncte IQ' : 'IQ Points'}
                </span>
              </div>

              {mastersPath && (
                <div className="header-account-progress">
                  <div className="header-account-progress-bar">
                    <div
                      className="header-account-progress-fill"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="header-account-progress-text">
                    {mastersPath.isMaxDefinedLevel ? (
                      language === 'ro'
                        ? 'Ai atins pragul maxim definit momentan.'
                        : 'You reached the highest defined threshold for now.'
                    ) : language === 'ro' ? (
                      <>
                        Încă <strong>{mastersPath.pointsToNext}</strong> puncte IQ până la nivelul{' '}
                        <strong>{userLevel + 1}</strong>.
                      </>
                    ) : (
                      <>
                        <strong>{mastersPath.pointsToNext}</strong> IQ Points to Level{' '}
                        <strong>{userLevel + 1}</strong>.
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="header-account-row">
                <div className="header-account-label">
                  {language === 'ro' ? 'Alias' : 'Nickname'}
                </div>
                <input
                  className="header-account-input"
                  value={nickDraft}
                  onChange={(e) => setNickDraft(e.target.value)}
                  placeholder={language === 'ro' ? 'ex: ChessHero' : 'e.g. ChessHero'}
                  autoComplete="nickname"
                />
              </div>

              {mastersPath && (
                <div className="header-account-inline-progress" title={progressTitle}>
                  <div className="header-account-inline-track">
                    <div
                      className="header-account-inline-fill"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="header-account-inline-text">
                    <span className="header-account-inline-pct">{progressPct}%</span>
                    <span className="header-account-inline-detail">{progressTitle}</span>
                  </div>
                </div>
              )}

              {(nickErr || nickMsg) && (
                <div className={nickErr ? 'header-account-msg error' : 'header-account-msg ok'}>
                  {nickErr || nickMsg}
                </div>
              )}

              <div className="header-account-actions">
                <button
                  className="header-account-secondary"
                  onClick={() => {
                    setNickDraft(String(nickname || '').trim());
                    setNickErr('');
                    setNickMsg('');
                    setMenuOpen(false);
                  }}
                >
                  {language === 'ro' ? 'Închide' : 'Close'}
                </button>

                <button
                  className="header-account-primary"
                  disabled={savingNick || !String(nickDraft || '').trim()}
                  onClick={async () => {
                    setSavingNick(true);
                    setNickErr('');
                    setNickMsg('');
                    try {
                      await upsertMyNickname(nickDraft);
                      if (typeof onProfileUpdated === 'function') await onProfileUpdated();
                      setNickMsg(language === 'ro' ? 'Alias salvat.' : 'Nickname saved.');
                      window.setTimeout(() => setMenuOpen(false), 700);
                    } catch (e) {
                      setNickErr(mapProfileErrorMessage(e));
                    } finally {
                      setSavingNick(false);
                    }
                  }}
                >
                  {savingNick ? (language === 'ro' ? 'Salvez...' : 'Saving...') : (language === 'ro' ? 'Salvează' : 'Save')}
                </button>
              </div>

              <button
                className="header-account-link"
              onClick={() => {
                setMenuOpen(false);
                if (typeof onOpenAccount === 'function') onOpenAccount('overview');
                else if (onRequestLogin) onRequestLogin();
              }}
            >
                {language === 'ro' ? 'Deschide pagina de cont' : 'Open account page'}
              </button>
            </div>
          )}
        </div>
      </div>

      {masterPathOpen && createPortal((
        <div
          className="header-masterpath-overlay"
          onClick={() => setMasterPathOpen(false)}
          role="presentation"
        >
          <div
            className="header-masterpath-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={masterPathCopy.title}
          >
            <button
              className="header-masterpath-close"
              onClick={() => setMasterPathOpen(false)}
              type="button"
              aria-label={masterPathCopy.close}
            >
              ×
            </button>
            <div className="header-masterpath-kicker">Master's Path</div>
            <h3 className="header-masterpath-title">{masterPathCopy.title}</h3>
            <p className="header-masterpath-subtitle">{masterPathCopy.subtitle}</p>
            <p className="header-masterpath-lead">{masterPathCopy.lead}</p>
            <ul className="header-masterpath-list">
              {masterPathCopy.bullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div className="header-masterpath-account-note">{masterPathCopy.accountNote}</div>
            <div className="header-masterpath-actions">
              <button
                className="header-masterpath-cta"
                type="button"
                onClick={() => {
                  setMasterPathOpen(false);
                  if (user && typeof onOpenAccount === 'function') onOpenAccount('overview');
                  else if (onRequestLogin) onRequestLogin();
                }}
              >
                {user ? masterPathCopy.accountCtaLogged : masterPathCopy.accountCta}
              </button>
            </div>
            <div className="header-masterpath-video-wrap">
              <video
                ref={masterVideoRef}
                className="header-masterpath-video"
                src="/media/calea-maestrului.mp4"
                autoPlay
                muted
                loop
                preload="metadata"
                playsInline
                onLoadedData={(e) => {
                  const v = e.currentTarget;
                  v.muted = true;
                  const maybePromise = v.play();
                  if (maybePromise && typeof maybePromise.catch === 'function') {
                    maybePromise.catch(() => {});
                  }
                }}
              />
            </div>
          </div>
        </div>
      ), document.body)}
    </header>
  );
}

export default Header;
